import { makeAutoObservable } from 'mobx'
import { RootStore } from 'stores'
import { IFilter } from 'types/common'
import {
  getLineBalanceById,
  getAllLineBalances,
  searchLineBalances,
} from 'API/plan'
import { IWorkPlan, IWorkPlanWithRelations, IWorkPlanDetailWithStations, IWorkplanSearchFilterDto } from 'interfaces/plan'

class UserWorkplanStore {
  rootStore: RootStore

  // Current workplan for execution
  currentWorkplan: IWorkPlanDetailWithStations = {} as IWorkPlanDetailWithStations

  // List of workplans available to user
  userWorkplans: IWorkPlanWithRelations[] = []

  // Selected workplan for viewing/execution
  selectedWorkplanId: number = -1
  selectedWorkplan: IWorkPlanWithRelations = {} as IWorkPlanWithRelations

  // User execution state
  currentStationId: number | null = null
  currentStepId: number | null = null
  completedSteps: number[] = []
  stationProgress: Record<number, { completedSteps: number[], totalSteps: number }> = {}

  // Loading states
  isFetchingList: boolean = false
  isFetchingDetail: boolean = false
  isExecutingStep: boolean = false

  // Pagination and count
  numberOfWorkplans: number = 0
  timeFetchingList: number = new Date().getTime()

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)
    this.rootStore = rootStore
  }

  // Setters
  public setSelectedWorkplanId(workplanId: number): void {
    this.selectedWorkplanId = workplanId
  }

  public setSelectedWorkplan(workplan: IWorkPlanWithRelations): void {
    this.selectedWorkplan = workplan
  }

  public setCurrentStationId(stationId: number | null): void {
    this.currentStationId = stationId
  }

  public setCurrentStepId(stepId: number | null): void {
    this.currentStepId = stepId
  }

  public markStepCompleted(stepId: number): void {
    if (!this.completedSteps.includes(stepId)) {
      this.completedSteps.push(stepId)
    }
  }

  public markStepIncomplete(stepId: number): void {
    this.completedSteps = this.completedSteps.filter(id => id !== stepId)
  }

  public updateStationProgress(stationId: number, completedSteps: number[], totalSteps: number): void {
    this.stationProgress[stationId] = { completedSteps, totalSteps }
  }

  // API calls for user workplan operations
  public async getUserWorkplans(filter: IFilter<IWorkPlan> = {}): Promise<void> {
    const currentTime: number = new Date().getTime()
    if (currentTime > this.timeFetchingList) {
      this.timeFetchingList = currentTime
    }

    this.isFetchingList = true
    try {
      // Filter for active/live workplans only (status 3-5 based on state machine)
      const userFilter = {
        ...filter,
        where: {
          ...filter.where,
          status: { inq: [3, 4, 5] } // Active, On Hold, Under Review
        }
      }
      const responseData = await getAllLineBalances(userFilter)
      if (this.timeFetchingList === currentTime) {
        this.userWorkplans = responseData
      }
    } finally {
      this.isFetchingList = false
    }
  }

  public async searchUserWorkplans(filterDto: IWorkplanSearchFilterDto): Promise<{ results: (IWorkPlanWithRelations & { workStationCount: number })[], totalCount: number }> {
    const currentTime: number = new Date().getTime()
    if (currentTime > this.timeFetchingList) {
      this.timeFetchingList = currentTime
    }

    this.isFetchingList = true
    try {
      // Filter for active workplans only
      const userFilterDto = {
        ...filterDto,
        status: 3 // Only active workplans for user execution
      }
      const responseData = await searchLineBalances(userFilterDto)
      if (this.timeFetchingList === currentTime) {
        this.userWorkplans = responseData.results
        this.numberOfWorkplans = responseData.totalCount
      }
      return responseData
    } finally {
      this.isFetchingList = false
    }
  }

  public async getWorkplanForExecution(workplanId: number): Promise<void> {
    this.isFetchingDetail = true
    try {
      const filter: IFilter<IWorkPlan> = {
        include: [
          {
            relation: 'workStations',
            scope: {
              include: [
                {
                  relation: 'steps',
                  scope: {
                    order: ['position ASC']
                  }
                }
              ],
              order: ['createdAt ASC']
            }
          }
        ]
      }
      
      const workplanDetail = await getLineBalanceById(workplanId, filter)
      this.currentWorkplan = workplanDetail
      
      // Initialize progress tracking
      this.initializeProgressTracking()
    } catch (error) {
      console.error('Failed to fetch workplan for execution:', error)
      throw error
    } finally {
      this.isFetchingDetail = false
    }
  }

  private initializeProgressTracking(): void {
    const workStations = this.currentWorkplan.workStations || []
    
    // Reset progress tracking
    this.stationProgress = {}
    this.completedSteps = []
    
    // Initialize station progress
    workStations.forEach(station => {
      if (station.id) {
        const totalSteps = station.steps?.length || 0
        this.updateStationProgress(station.id, [], totalSteps)
      }
    })
    
    // Set first station as current if exists
    if (workStations.length > 0 && workStations[0].id) {
      this.setCurrentStationId(workStations[0].id)
      
      // Set first step as current if exists
      const firstStation = workStations[0]
      if (firstStation.steps && firstStation.steps.length > 0 && firstStation.steps[0].stepId) {
        this.setCurrentStepId(firstStation.steps[0].stepId)
      }
    }
  }

  // Execution flow methods
  public async startStepExecution(stepId: number): Promise<void> {
    this.isExecutingStep = true
    try {
      // Set current step
      this.setCurrentStepId(stepId)
      
      // Here you would typically call an API to log step start
      // For now, we'll just update the UI state
      
    } finally {
      this.isExecutingStep = false
    }
  }

  public async completeStepExecution(stepId: number): Promise<void> {
    this.isExecutingStep = true
    try {
      // Mark step as completed
      this.markStepCompleted(stepId)
      
      // Update station progress
      const currentStation = this.getCurrentStation()
      if (currentStation && currentStation.id) {
        const stationSteps = currentStation.steps || []
        const completedStationSteps = stationSteps
          .filter(step => step.stepId && this.completedSteps.includes(step.stepId))
          .map(step => step.stepId!)
        
        this.updateStationProgress(currentStation.id, completedStationSteps, stationSteps.length)
      }
      
      // Move to next step automatically
      this.moveToNextStep()
      
      // Here you would typically call an API to log step completion
      
    } finally {
      this.isExecutingStep = false
    }
  }

  public moveToNextStep(): void {
    const currentStation = this.getCurrentStation()
    if (!currentStation || !currentStation.steps) return
    
    const currentStepIndex = currentStation.steps.findIndex(step => step.stepId === this.currentStepId)
    if (currentStepIndex >= 0 && currentStepIndex < currentStation.steps.length - 1) {
      // Move to next step in current station
      const nextStep = currentStation.steps[currentStepIndex + 1]
      if (nextStep.stepId) {
        this.setCurrentStepId(nextStep.stepId)
      }
    } else {
      // Current station completed, move to next station
      this.moveToNextStation()
    }
  }

  public moveToNextStation(): void {
    const workStations = this.currentWorkplan.workStations || []
    const currentStationIndex = workStations.findIndex(station => station.id === this.currentStationId)
    
    if (currentStationIndex >= 0 && currentStationIndex < workStations.length - 1) {
      // Move to next station
      const nextStation = workStations[currentStationIndex + 1]
      if (nextStation.id) {
        this.setCurrentStationId(nextStation.id)
        
        // Set first step of next station as current
        if (nextStation.steps && nextStation.steps.length > 0 && nextStation.steps[0].stepId) {
          this.setCurrentStepId(nextStation.steps[0].stepId)
        }
      }
    } else {
      // All stations completed
      this.setCurrentStationId(null)
      this.setCurrentStepId(null)
    }
  }

  // Helper methods
  public getCurrentStation() {
    return this.currentWorkplan.workStations?.find(station => station.id === this.currentStationId)
  }

  public getStationProgress(stationId: number) {
    return this.stationProgress[stationId] || { completedSteps: [], totalSteps: 0 }
  }

  public getOverallProgress() {
    const totalSteps = Object.values(this.stationProgress).reduce((sum, progress) => sum + progress.totalSteps, 0)
    const completedStepsCount = this.completedSteps.length
    return totalSteps > 0 ? (completedStepsCount / totalSteps) * 100 : 0
  }

  public isStepCompleted(stepId: number): boolean {
    return this.completedSteps.includes(stepId)
  }

  public isStationCompleted(stationId: number): boolean {
    const progress = this.getStationProgress(stationId)
    return progress.completedSteps.length === progress.totalSteps && progress.totalSteps > 0
  }

  public canAccessStep(stepId: number): boolean {
    // For now, allow access to all steps
    // In the future, you might implement sequential step access
    return true
  }
}

export default UserWorkplanStore
